<template>
  <template v-if="isVisible">
    <a
        v-if="phone"
        ref="revealedLinkRef"
        :href="telHref"
        :class="['button', 'clinic-call-button', classList]"
        :aria-label="`Позвонить в ${clinic.name}: ${formattedPhone}`"
        v-analytics:call_phone="analyticsClickParams"
        @click="copyPhone"
    >
      <SvgoPhone class="clinic-call-button__icon" aria-hidden="true"/>
      <span class="button-text">{{ formattedPhone }}</span>
    </a>
    <Button
        v-else
        :class-list="classList"
        :is-disabled="loading"
        :text="loading ? 'Показываем…' : 'Позвонить'"
        :aria-label="loading ? `Показываем номер клиники ${clinic.name}…` : `Позвонить в ${clinic.name}`"
        is-button
        @click="reveal"
    />
  </template>
</template>

<script lang="ts" setup>
import type {TClinic} from "~/types/clinic";
import {formatPhoneNumber} from "~/utils/formater";
import {extractApiErrorMessage, showToast} from "~/composables/useToast";
import {loadMangoWidget, MangoWidgetError, requestMangoNumber, type TMangoFailureReason} from "~/utils/mangoWidget";

const props = defineProps<{
  clinic: TClinic;
  source: 'list' | 'detail' | 'map';
  classList?: string;
}>();

const {apiQuery} = useApi();
const {$analytics} = useNuxtApp();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();

const sourceMap: Record<'list' | 'detail' | 'map', 'list' | 'clinic_page' | 'map'> = {
  list: 'list',
  detail: 'clinic_page',
  map: 'map',
};

const phone = ref<string | null>(null);
const loading = ref(false);
const revealedLinkRef = ref<HTMLAnchorElement | null>(null);
let expiryTimer: ReturnType<typeof setTimeout> | undefined;
let serverTimeOffsetMs = 0;

const {copyToClipboard} = useClipboard();

// navigator.clipboard.writeText требует фокус документа, а нативный диалер tel: его забирает — копирование должно завершиться до навигации.
const copyPhone = async (event: MouseEvent): Promise<void> => {
  if (!phone.value) return;
  event.preventDefault();
  try {
    await copyToClipboard(phone.value, {successMessage: 'Номер скопирован'});
  } finally {
    window.location.href = telHref.value;
  }
};

const telHref = computed(() => phone.value ? `tel:${phone.value.replace(/[^+\d]/g, '')}` : '');
const formattedPhone = computed(() => phone.value ? formatPhoneNumber(phone.value) : '');

const analyticsPage = computed(() => route.path.startsWith('/uslugi/') ? 'services' : 'clinics');
const analyticsClickParams = computed(() => ({
  content: 'call',
  context: props.clinic.name,
  source: props.source,
  clinicId: props.clinic.id,
}));

const clearExpiryTimer = () => {
  if (expiryTimer !== undefined) {
    clearTimeout(expiryTimer);
    expiryTimer = undefined;
  }
};

const serverNow = () => Date.now() + serverTimeOffsetMs;

const captureServerTimeOffset = (dateHeader: string | null) => {
  if (!dateHeader) return;
  const serverTime = new Date(dateHeader).getTime();
  if (Number.isFinite(serverTime)) {
    serverTimeOffsetMs = serverTime - Date.now();
  }
};

// Показанный номер живёт только до конца окна закрепления у Манго: дольше открытая
// вкладка не должна предлагать звонок на номер, который Манго уже мог отдать другой клинике.
const scheduleExpiry = (reservedUntil: string) => {
  clearExpiryTimer();
  const delay = new Date(reservedUntil).getTime() - serverNow();
  if (!Number.isFinite(delay) || delay <= 0) {
    phone.value = null;
    return;
  }
  expiryTimer = setTimeout(() => {
    phone.value = null;
  }, delay);
};

const trackNumberShown = () => {
  $analytics?.({
    event: 'event',
    page: analyticsPage.value,
    action: 'open',
    position: 'call_phone',
    content: 'number_shown',
    context: props.clinic.name,
    source: props.source,
    clinicId: props.clinic.id,
  });
};

// Совпадает с плейсхолдером в .env.example — стенд, где переменную забыли заменить
// на реальный id, не должен рендерить кнопку, ведущую на заведомо нерабочий виджет.
const UNCONFIGURED_MANGO_WIDGET_IDS = new Set(['', 'REPLACE_WITH_MANGO_WIDGET_ID']);
const isMangoConfigured = computed(() => !UNCONFIGURED_MANGO_WIDGET_IDS.has(String(runtimeConfig.public.mangoWidgetId ?? '').trim()));
const isVisible = computed(() =>
    props.source === 'detail'
        || (props.source === 'list' && props.clinic.hasReceptionPhone === true)
        || (props.clinic.hasReceptionPhone === true && isMangoConfigured.value),
);

const reportMangoFailure = async (intentId: string, reason: TMangoFailureReason): Promise<void> => {
  try {
    await apiQuery('public/call-intents/failure', 'POST', {
      body: {intent_id: intentId, reason},
      retry: 0,
    });
  } catch {
    console.warn('[clinic-call] Не удалось сообщить об ошибке выдачи номера');
  }
};

const reveal = async () => {
  if (phone.value || loading.value) return;
  const widgetId = String(runtimeConfig.public.mangoWidgetId ?? '').trim();
  let intentId: string | undefined;
  loading.value = true;
  try {
    const intent = await apiQuery<{ intent_id: string; hash: string; redirect_number: string; expires_at: string }>(
        'public/call-intents',
        'POST',
        {body: {clinic_id: props.clinic.id, source: sourceMap[props.source]}, retry: 0},
    );
    intentId = intent.intent_id;
    if (!isMangoConfigured.value) {
      throw new MangoWidgetError('Виджет Манго не настроен', 'widget_not_configured');
    }

    const widget = await loadMangoWidget(widgetId, String(runtimeConfig.public.mangoMockNumber ?? ''));
    const result = await requestMangoNumber(widget, intent.hash, intent.redirect_number);

    const show = await apiQuery<{ phone_number: string; reserved_until: string }>(
        'public/call-intents/confirm',
        'POST',
        {
          body: {
            intent_id: intent.intent_id,
            phone_number: result.number,
          },
          retry: 0,
          onResponse: ({response}: { response: Response }) => captureServerTimeOffset(response.headers.get('date')),
        },
    );

    phone.value = show.phone_number;
    scheduleExpiry(show.reserved_until);
    if (phone.value) {
      trackNumberShown();
      // <a> заменяет <button> в DOM: без переноса фокуса вручную второй Enter/Tab уходит в body.
      await nextTick();
      revealedLinkRef.value?.focus();
    }
  } catch (error) {
    console.error('[clinic-call] Не удалось получить номер клиники', error);
    const reason: TMangoFailureReason | undefined = error instanceof MangoWidgetError
        ? error.reason
        : error && typeof error === 'object' && 'code' in error && error.code === 'dct_number_unavailable'
            ? 'number_rejected'
            : undefined;
    if (intentId && reason) {
      void reportMangoFailure(intentId, reason);
    }
    if (error instanceof MangoWidgetError) {
      showToast('Звонок сейчас недоступен', 'error');
    } else {
      showToast(extractApiErrorMessage(error, 'Не удалось показать номер. Попробуйте ещё раз'), 'error');
    }
  } finally {
    loading.value = false;
  }
};

onBeforeUnmount(clearExpiryTimer);
</script>

<style lang="scss">
.clinic-call-button {
  text-decoration: none;
}
</style>
